import { EntityDB } from '@babycommando/entity-db';
import { env } from '@huggingface/transformers';
import { appState } from './appState';
import { store } from './store';
import { storage } from './storage';
// Configure environment to use full HuggingFace URL
env.allowLocalModels = false;
env.useBrowserCache = true;
env.remoteHost = 'https://huggingface.co';
const EMBEDDING_MODEL = 'Xenova/all-MiniLM-L6-v2';
const RECENT_MESSAGE_COUNT = 2;
const FALLBACK_MESSAGE_COUNT = 10;
const SEMANTIC_HITS = 5;
const SEMANTIC_QUERY_LIMIT = 20;
const EMBEDDING_IDLE_TIMEOUT = 2000;
const MEMORY_INDEX_PREFIX = 'blindquery_memory_index_';
let memoryDb = null;
let embeddingQueue = [];
let embeddingFlushScheduled = false;
let embeddingInFlight = false;
const queuedMessageIds = new Set();
let memoryIndexCache = null;
function getMessageContent(message) {
    if (message.role === 'user') {
        return message.anonymizedContent || '';
    }
    return message.content;
}
function toChatCompletionMessage(message) {
    const content = getMessageContent(message).trim();
    if (!content) {
        return null;
    }
    return {
        role: message.role === 'ai' ? 'assistant' : 'user',
        content
    };
}
function formatSemanticMemory(hits) {
    const lines = hits.map(hit => `- ${hit.role === 'ai' ? 'assistant' : 'user'}: ${hit.content}`);
    return `Relevant memory (anonymized):\n${lines.join('\n')}`;
}
function buildDatabaseName(userId) {
    return `blindquery_memory_${userId}`;
}
function buildMemoryIndexKey(userId) {
    return `${MEMORY_INDEX_PREFIX}${userId}`;
}
function getEffectiveUserId() {
    const user = store.getUser();
    return user?.id ?? 'anonymous';
}
async function getMemoryIndex() {
    const userId = getEffectiveUserId();
    if (memoryIndexCache) {
        return memoryIndexCache;
    }
    const stored = await storage.load(buildMemoryIndexKey(userId));
    memoryIndexCache = stored ?? {};
    return memoryIndexCache;
}
async function saveMemoryIndex(index) {
    const userId = getEffectiveUserId();
    memoryIndexCache = index;
    await storage.save(buildMemoryIndexKey(userId), index);
}
async function getMemoryKey(projectId, chatId, messageId) {
    const index = await getMemoryIndex();
    return index[projectId]?.[chatId]?.[messageId] ?? null;
}
async function setMemoryKey(projectId, chatId, messageId, key) {
    const index = await getMemoryIndex();
    const projectIndex = index[projectId] ?? {};
    const chatIndex = projectIndex[chatId] ?? {};
    chatIndex[messageId] = key;
    projectIndex[chatId] = chatIndex;
    index[projectId] = projectIndex;
    await saveMemoryIndex(index);
}
async function getMemoryDatabase() {
    if (memoryDb) {
        return memoryDb;
    }
    const userId = getEffectiveUserId();
    try {
        memoryDb = new EntityDB({
            vectorPath: buildDatabaseName(userId),
            model: EMBEDDING_MODEL
        });
        return memoryDb;
    }
    catch (error) {
        console.error('Failed to initialize semantic memory', error);
        return null;
    }
}
async function addMemoryRecord(record) {
    const database = await getMemoryDatabase();
    if (!database) {
        throw new Error('Semantic memory database unavailable');
    }
    return database.insert(record);
}
function scheduleEmbeddingFlush() {
    if (embeddingFlushScheduled) {
        return;
    }
    embeddingFlushScheduled = true;
    const flush = () => {
        embeddingFlushScheduled = false;
        void flushEmbeddingQueue();
    };
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(flush, { timeout: EMBEDDING_IDLE_TIMEOUT });
    }
    else {
        setTimeout(flush, 600);
    }
}
async function flushEmbeddingQueue() {
    if (embeddingInFlight) {
        return;
    }
    embeddingInFlight = true;
    try {
        while (embeddingQueue.length > 0) {
            const record = embeddingQueue.shift();
            if (!record) {
                continue;
            }
            const existingKey = await getMemoryKey(record.projectId, record.chatId, record.messageId);
            if (existingKey !== null) {
                continue;
            }
            const key = await addMemoryRecord(record);
            await setMemoryKey(record.projectId, record.chatId, record.messageId, key);
        }
    }
    catch (error) {
        console.error('Failed to process semantic memory embeddings', error);
    }
    finally {
        embeddingInFlight = false;
        if (embeddingQueue.length > 0) {
            scheduleEmbeddingFlush();
        }
    }
}
function normalizeMemoryRecord(hit) {
    const text = typeof hit.text === 'string'
        ? hit.text
        : typeof hit.content === 'string'
            ? hit.content
            : '';
    if (!text) {
        return null;
    }
    const role = hit.role === 'ai' ? 'ai' : 'user';
    return {
        userId: typeof hit.userId === 'string' ? hit.userId : '',
        projectId: typeof hit.projectId === 'string' ? hit.projectId : '',
        chatId: typeof hit.chatId === 'string' ? hit.chatId : '',
        messageId: typeof hit.messageId === 'string' ? hit.messageId : '',
        role,
        timestamp: typeof hit.timestamp === 'string' ? hit.timestamp : '',
        text,
        content: text
    };
}
function filterByScope(record, projectId, chatId) {
    if (appState.memoryScope === 'chat') {
        return record.projectId === projectId && record.chatId === chatId;
    }
    return record.projectId === projectId;
}
async function searchSemanticMemory(queryText, projectId, chatId) {
    const database = await getMemoryDatabase();
    if (!database) {
        throw new Error('Semantic memory database unavailable');
    }
    const results = await database.query(queryText, { limit: SEMANTIC_QUERY_LIMIT });
    const normalized = results
        .map((hit) => normalizeMemoryRecord(hit))
        .filter((hit) => Boolean(hit));
    const filtered = normalized.filter((hit) => filterByScope(hit, projectId, chatId));
    return filtered.slice(0, SEMANTIC_HITS);
}
function normalizeTimestamp(value) {
    if (typeof value === 'string') {
        return value;
    }
    return value.toISOString();
}
function buildMemoryRecord(projectId, chatId, message) {
    const content = getMessageContent(message).trim();
    if (!content) {
        return null;
    }
    const userId = getEffectiveUserId();
    return {
        userId,
        projectId,
        chatId,
        messageId: message.id,
        role: message.role,
        timestamp: normalizeTimestamp(message.timestamp),
        text: content,
        content
    };
}
export function queueMemoryForMessage(projectId, chatId, message) {
    if (!appState.memoryEnabled) {
        return;
    }
    if (queuedMessageIds.has(message.id)) {
        return;
    }
    const record = buildMemoryRecord(projectId, chatId, message);
    if (!record) {
        return;
    }
    queuedMessageIds.add(message.id);
    embeddingQueue.push(record);
    scheduleEmbeddingFlush();
}
export function queueChatForMemory(projectId, chatId, messages) {
    if (!appState.memoryEnabled) {
        return;
    }
    messages.forEach(message => queueMemoryForMessage(projectId, chatId, message));
}
async function deleteMemoryKeys(keys) {
    if (keys.length === 0) {
        return;
    }
    const database = await getMemoryDatabase();
    if (!database) {
        return;
    }
    await Promise.all(keys.map(async (key) => {
        try {
            await database.delete(key);
        }
        catch (error) {
            console.warn('Failed to delete semantic memory record', error);
        }
    }));
}
export async function deleteChatMemory(projectId, chatId) {
    const index = await getMemoryIndex();
    const chatIndex = index[projectId]?.[chatId];
    if (!chatIndex) {
        return;
    }
    const keys = Object.values(chatIndex);
    await deleteMemoryKeys(keys);
    delete index[projectId][chatId];
    if (Object.keys(index[projectId]).length === 0) {
        delete index[projectId];
    }
    await saveMemoryIndex(index);
}
export async function deleteProjectMemory(projectId) {
    const index = await getMemoryIndex();
    const projectIndex = index[projectId];
    if (!projectIndex) {
        return;
    }
    const keys = Object.values(projectIndex).flatMap(chatIndex => Object.values(chatIndex));
    await deleteMemoryKeys(keys);
    delete index[projectId];
    await saveMemoryIndex(index);
}
function buildMessageContent(message, files) {
    const textContent = getMessageContent(message).trim();
    const imageFiles = files.filter(f => f.isImage && f.imageData);
    // If no images, return just text
    if (imageFiles.length === 0) {
        return textContent;
    }
    // Build multimodal content with text and images
    const content = [];
    // Add text content
    if (textContent) {
        content.push({ type: 'text', text: textContent });
    }
    // Add images
    for (const file of imageFiles) {
        content.push({
            type: 'image',
            image: file.imageData
        });
    }
    return content;
}
export async function buildModelContext(projectId, chatId, currentMessage, files = []) {
    const basePrompt = appState.currentSystemPrompt || 'You are a helpful assistant.';
    const chat = store.getChat(projectId, chatId);
    const history = chat?.messages ?? [];
    const previousMessages = history.filter(message => message.id !== currentMessage.id);
    let useFallback = false;
    let semanticHits = [];
    let recentMessages = previousMessages.slice(-RECENT_MESSAGE_COUNT);
    if (appState.memoryEnabled) {
        try {
            const queryText = getMessageContent(currentMessage).trim();
            if (queryText) {
                semanticHits = await searchSemanticMemory(queryText, projectId, chatId);
            }
        }
        catch (error) {
            console.warn('Semantic memory unavailable, using fallback context.', error);
            useFallback = true;
        }
    }
    if (appState.memoryEnabled && useFallback) {
        recentMessages = previousMessages.slice(-FALLBACK_MESSAGE_COUNT);
        semanticHits = [];
    }
    const recentIds = new Set(recentMessages.map(message => message.id));
    const seenHitIds = new Set();
    const dedupedHits = semanticHits.filter(hit => {
        if (recentIds.has(hit.messageId) || hit.messageId === currentMessage.id) {
            return false;
        }
        if (seenHitIds.has(hit.messageId)) {
            return false;
        }
        seenHitIds.add(hit.messageId);
        return true;
    });
    const memoryBlock = appState.memoryEnabled && dedupedHits.length > 0
        ? formatSemanticMemory(dedupedHits)
        : '';
    const systemPrompt = memoryBlock ? `${basePrompt}\n\n${memoryBlock}` : basePrompt;
    // Build messages - history is text-only, current message may include images
    const messages = [];
    // Add recent messages (text-only)
    for (const msg of recentMessages) {
        const chatMsg = toChatCompletionMessage(msg);
        if (chatMsg)
            messages.push(chatMsg);
    }
    // Add current message with images if present
    const currentContent = buildMessageContent(currentMessage, files);
    if (currentContent) {
        messages.push({
            role: 'user',
            content: currentContent
        });
    }
    return {
        systemPrompt,
        messages
    };
}
